import { useEffect, useState } from "react";
import { api } from "../api.js";
import { ErrorBox, SuccessBox } from "../components/helpers.jsx";

const MODES = [
    { v: "off",  label: "Off",         hint: "Don't fire; drop the finding" },
    { v: "flag", label: "Flag only",   hint: "Record but don't act; counts toward the flag threshold" },
    { v: "deny", label: "Deny",        hint: "Reject this load; subject can retry" },
    { v: "ban",  label: "Instant ban", hint: "Ban + cascade HWID identifiers" },
];

const HWID_KIND_NOTES = {
    bcd:           "BCD-Template SHA256 — primary HWID, strongest single signal",
    smbios_uuid:   "SMBIOS Type-1 system UUID",
    mobo_sn:       "Motherboard serial",
    sys_sn:        "System serial",
    ram_sn:        "Per-DIMM serial — only real serials, blanks ignored",
    disk_sn:       "Per physical drive serial",
    mon_sn:        "EDID serial string — shared across same model, weak",
    mon_sha:       "EDID hash — collides on identical monitors",
    cpu_sn:        "CPU serial — consumer CPUs almost always blank",
    mouse_sn:      "HID mouse serial — same-model mice share it",
    kbd_sn:        "HID keyboard serial",
    mac:           "MAC address — randomization + virtual NICs make it unreliable alone",
    vol_vsn:       "Volume serial — wiped on reformat",
    gpu_luid:      "DXGI LUID — re-rolls per session, near useless alone",
    tpm_ekpub_sha: "TPM EKpub SHA256 — strong, hard to spoof",
    vol_guid:      "\\??\\Volume{...} GUID — survives format",
    neighbor_mac:  "ARP neighbour MACs — network environment, not identity",
};

function ModeSelect({ value, onChange }) {
    return (
        <select value={value} onChange={(e) => onChange(e.target.value)}>
            {MODES.map((m) => (
                <option key={m.v} value={m.v}>{m.label}</option>
            ))}
        </select>
    );
}

function ModePill({ mode }) {
    const map = {
        off:  { cls: "lifted", label: "off" },
        flag: { cls: "stored", label: "flag" },
        deny: { cls: "deny",   label: "deny" },
        ban:  { cls: "ban",    label: "ban" },
    };
    const p = map[mode] ?? map.off;
    return <span className={`pill ${p.cls}`}>{p.label}</span>;
}

export default function Config() {
    const [server, setServer] = useState(null);   // { config, overrides, defaults }
    const [draft,  setDraft]  = useState(null);   // editable shape (full config)
    const [err, setErr] = useState(null);
    const [msg, setMsg] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let alive = true;
        api.get("/api/admin/me/config")
            .then((d) => {
                if (!alive) return;
                setServer(d);
                setDraft(JSON.parse(JSON.stringify(d.config)));
            })
            .catch((e) => { if (alive) setErr(e.message); });
        return () => { alive = false; };
    }, []);

    if (!draft || !server) {
        return (
            <>
                <div className="page-header"><h1>Detection rules</h1></div>
                <ErrorBox>{err}</ErrorBox>
                {!err && <div className="text-dim">Loading…</div>}
            </>
        );
    }

    function update(path, value) {
        setMsg(null);
        setDraft((d) => {
            const next = { ...d };
            const segs = path.split(".");
            let cur = next;
            for (let i = 0; i < segs.length - 1; i++) {
                cur[segs[i]] = { ...cur[segs[i]] };
                cur = cur[segs[i]];
            }
            cur[segs[segs.length - 1]] = value;
            return next;
        });
    }

    async function save() {
        setBusy(true); setErr(null); setMsg(null);
        try {
            const body = {
                hwidBanThreshold:    Number(draft.hwidBanThreshold),
                flagBanThreshold:    Number(draft.flagBanThreshold),
                replayWindowSeconds: Number(draft.replayWindowSeconds),
                hwidWeights:         draft.hwidWeights,
                categories:          draft.categories,
            };
            const r = await api.post("/api/admin/me/config", body);
            setServer(r);
            setDraft(JSON.parse(JSON.stringify(r.config)));
            setMsg("Saved. Live for the next /api/v1/process call.");
        } catch (e) { setErr(e.message); }
        finally { setBusy(false); }
    }

    async function resetAll() {
        if (!confirm("Reset every rule to defaults? Your current overrides will be wiped.")) return;
        setBusy(true); setErr(null); setMsg(null);
        try {
            const r = await api.post("/api/admin/me/config/reset");
            setServer(r);
            setDraft(JSON.parse(JSON.stringify(r.config)));
            setMsg("All rules reset to defaults.");
        } catch (e) { setErr(e.message); }
        finally { setBusy(false); }
    }

    const defs = server.defaults;
    const isHwidDefault = (k) => draft.hwidWeights[k] === defs.hwidWeights[k];
    const isCatDefault  = (id, defMode) => draft.categories[id]?.mode === defMode;

    const flagCount = Object.values(draft.categories).filter((c) => c.mode === "flag").length;

    return (
        <>
            <div className="page-header">
                <div>
                    <h1>Detection rules</h1>
                    <div className="sub">Everything here applies only to your tenant — admins don't see each other's settings.</div>
                </div>
                <div className="row">
                    <button onClick={resetAll} disabled={busy}>Reset to defaults</button>
                    <button className="primary" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save changes"}</button>
                </div>
            </div>

            <ErrorBox>{err}</ErrorBox>
            <SuccessBox>{msg}</SuccessBox>

            {/* Thresholds */}
            <div className="card">
                <h2>Thresholds</h2>
                <div className="grid cols-3" style={{ marginTop: 8 }}>
                    <div>
                        <label>HWID ban threshold</label>
                        <input type="number" step="0.05" min="0.05" max="10"
                               value={draft.hwidBanThreshold}
                               onChange={(e) => update("hwidBanThreshold", e.target.value)} />
                        <div className="text-dim" style={{ fontSize: 12, marginTop: 4 }}>
                            Sum of any single banned subject's matched weights must reach this. Default {defs.hwidBanThreshold}.
                        </div>
                    </div>
                    <div>
                        <label>Flag → ban threshold</label>
                        <input type="number" step="1" min="0" max="50"
                               value={draft.flagBanThreshold}
                               onChange={(e) => update("flagBanThreshold", e.target.value)} />
                        <div className="text-dim" style={{ fontSize: 12, marginTop: 4 }}>
                            Once <strong>{Number(draft.flagBanThreshold) || "N"}</strong> flag-mode signals accumulate in a single load → ban.
                            Set to <strong>0</strong> to disable. <span className="text-muted">Currently {flagCount} category{flagCount === 1 ? "" : "ies"} in flag mode.</span>
                        </div>
                    </div>
                    <div>
                        <label>Replay window (seconds)</label>
                        <input type="number" step="1" min="1" max="86400"
                               value={draft.replayWindowSeconds}
                               onChange={(e) => update("replayWindowSeconds", e.target.value)} />
                        <div className="text-dim" style={{ fontSize: 12, marginTop: 4 }}>
                            Allowed drift between client <code>ts</code> and server clock. Default {defs.replayWindowSeconds}s.
                        </div>
                    </div>
                </div>
            </div>

            {/* Categories */}
            <div className="card" style={{ marginTop: 12 }}>
                <h2>Detection categories</h2>
                <p className="text-dim" style={{ margin: "4px 0 12px", fontSize: 13 }}>
                    Pick how each analyzer signal behaves. <strong>Flag</strong> records the finding without acting;
                    <strong> deny</strong> rejects the load; <strong>ban</strong> instant-bans the subject and cascades
                    their HWID identifiers. <code>already_banned</code>, <code>missing_telemetry</code>, and
                    <code> missing_ts</code> are hardcoded structural checks and aren't configurable.
                </p>
                <table className="data" style={{ marginTop: 8 }}>
                    <thead>
                        <tr>
                            <th>Category</th>
                            <th style={{ width: 150 }}>Mode</th>
                            <th>What it detects</th>
                            <th style={{ width: 100 }}>Default</th>
                        </tr>
                    </thead>
                    <tbody>
                        {defs.categories.map((c) => (
                            <tr key={c.id}>
                                <td className="mono">{c.id}</td>
                                <td>
                                    <ModeSelect
                                        value={draft.categories[c.id]?.mode ?? c.default}
                                        onChange={(v) => update(`categories.${c.id}.mode`, v)}
                                    />
                                </td>
                                <td className="text-dim">{c.description}</td>
                                <td>
                                    <ModePill mode={c.default} />
                                    {!isCatDefault(c.id, c.default) && (
                                        <button className="ghost" style={{ marginLeft: 6, fontSize: 11 }}
                                                onClick={() => update(`categories.${c.id}.mode`, c.default)}>
                                            revert
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* HWID weights */}
            <div className="card" style={{ marginTop: 12 }}>
                <h2>HWID weights</h2>
                <p className="text-dim" style={{ margin: "4px 0 12px", fontSize: 13 }}>
                    Each matched identifier kind contributes its weight to the total against any single previously-banned
                    subject. Total ≥ <strong>{draft.hwidBanThreshold}</strong> → ban. Zero a weight to ignore that kind
                    entirely (useful for noisy or shared identifiers).
                </p>
                <table className="data" style={{ marginTop: 8 }}>
                    <thead>
                        <tr>
                            <th>Kind</th>
                            <th style={{ width: 120 }}>Weight</th>
                            <th>Notes</th>
                            <th style={{ width: 100 }}>Default</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Object.keys(defs.hwidWeights).map((k) => (
                            <tr key={k}>
                                <td className="mono">{k}</td>
                                <td>
                                    <input type="number" step="0.05" min="0" max="10"
                                           value={draft.hwidWeights[k]}
                                           onChange={(e) => update(`hwidWeights.${k}`, Number(e.target.value))} />
                                </td>
                                <td className="text-dim">{HWID_KIND_NOTES[k]}</td>
                                <td>
                                    <span className="mono">{defs.hwidWeights[k]}</span>
                                    {!isHwidDefault(k) && (
                                        <button className="ghost" style={{ marginLeft: 6, fontSize: 11 }}
                                                onClick={() => update(`hwidWeights.${k}`, defs.hwidWeights[k])}>
                                            revert
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="row" style={{ marginTop: 16, justifyContent: "flex-end" }}>
                <button onClick={resetAll} disabled={busy}>Reset to defaults</button>
                <button className="primary" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save changes"}</button>
            </div>
        </>
    );
}
